import React from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import { get } from 'lodash';

import { t } from '../../../../../../modules/infra/i18n';
import Input from '../../../../../common/input';
import Checkbox from '../../../../../common/simpleCheckbox';
import CurrencyInput from '../../../../../common/currencyInput';
import { inputParseFn } from '../../../../../common/helpers/inputs';
import InventoryCell from './InventoryCell';

const InventoryStep = ({
  product,
  toggleLocations,
  toggleLocation,
  unallocatedInventory,
  assignmentValues,
  shopifySettings,
  hasOverrides,
  overrides,
  matchedItems,
  resetOverrides,
}) => (
  <div className="u-max-width-1000 u-margin-auto">
    <div className="c-inner-wrapper u-pt+ c-brand-inventory-table">
      <ReactTable
        data={[
          {
            ...product,
            isTopLevel: true,
          },
          ...product.assignments,
        ]}
        defaultPageSize={product.assignments.length + 1}
        pageSize={product.assignments.length + 1}
        showPagination={false}
        sortable={false}
        minRows={0}
        className="c-table-new c-no-hover"
        getTdProps={() => ({
          className: 'u-p-- u-m--',
        })}
        getTheadThProps={() => ({
          className: 'u-p-- u-m--',
        })}
        columns={[
          {
            id: 'name',
            Header: t('products:inventory_step.location_header'),
            width: 250,
            Cell: row => (
              row.original.isTopLevel ? (
                <Checkbox
                  id="allEnabled"
                  name="allEnabled"
                  label={t('products:inventory_step.all_locations_label')}
                  onChange={toggleLocations}
                />
              ) : (
                <div className="u-pl+" data-automation-id={`assignments.${row.original.ownerId}`}>
                  <Checkbox
                    label={row.original.name}
                    id={`assignments.${row.original.ownerId}.enabled`}
                    name={`assignments.${row.original.ownerId}.enabled`}
                    onChange={() => toggleLocation(row.original.ownerId)}
                  />
                </div>
              )
            ),
          },
          {
            id: 'totalUnallocated',
            Header: t('products:inventory_step.inventory_header'),
            Cell: row => (
              <InventoryCell
                row={row}
                unallocatedInventory={unallocatedInventory}
                assignmentValues={assignmentValues}
                shopifySettings={shopifySettings}
                matchedItems={matchedItems}
              />
            ),
          },
          {
            id: 'lowInventoryThreshold',
            Header: t('products:inventory_step.low_inventory_header'),
            Cell: row => (
              <Input
                type="number"
                className="c-input"
                placeholder={t('products:inventory_step.low_inventory_placeholder')}
                name={row.original.isTopLevel
                  ? 'lowInventoryThreshold' : `assignments.${row.original.ownerId}.lowInventoryThreshold`}
                parse={inputParseFn}
                disabled={!get(assignmentValues, [row.original.ownerId, 'enabled'], false)}
                min={0}
              />
            ),
          },
          {
            id: 'highInventoryThreshold',
            Header: t('products:inventory_step.high_inventory_header'),
            Cell: row => (
              <Input
                type="number"
                className="c-input"
                placeholder={t('products:inventory_step.high_inventory_placeholder')}
                name={row.original.isTopLevel
                  ? 'highInventoryThreshold' : `assignments.${row.original.ownerId}.highInventoryThreshold`}
                parse={inputParseFn}
                disabled={!get(assignmentValues, [row.original.ownerId, 'enabled'], false)}
                min={0}
              />
            ),
          },
          {
            id: 'locationPrice',
            Header: t('products:inventory_step.retail_price_header'),
            width: 100,
            Cell: row => (
              <CurrencyInput
                className="c-input"
                name={row.original.isTopLevel ? 'locationPrice' : `assignments.${row.original.ownerId}.price`}
                disabled={!get(assignmentValues, [row.original.ownerId, 'enabled'], false)}
              />
            ),
          },
          {
            id: 'customization',
            Header: t('products:inventory_step.actions_header'),
            width: 100,
            Cell: (row) => {
              const showButton = row.original.isTopLevel
                ? hasOverrides
                : !!overrides[row.original.ownerId];

              return showButton ? (
                <button
                  type="button"
                  className="c-btn c-btn--blank c-btn--link pointer-events u-p-0"
                  onClick={() => resetOverrides(
                    !row.original.isTopLevel && row.original.ownerId, Object.keys(overrides).length
                  )}
                >
                  <span className="c-btn__text">
                    {row.original.isTopLevel 
                      ? t('products:inventory_step.reset_all_button')
                      : t('products:inventory_step.reset_button')
                    }
                  </span>
                </button>
                )
                : null;
            },
          },
        ]}
      />
    </div>
  </div>
);

InventoryStep.propTypes = {
  unallocatedInventory: PropTypes.number.isRequired,
  product: PropTypes.shape({}).isRequired,
  toggleLocations: PropTypes.func.isRequired,
  toggleLocation: PropTypes.func.isRequired,
  assignmentValues: PropTypes.shape({}).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  shopifySettings: PropTypes.object.isRequired,
  matchedItems: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasOverrides: PropTypes.bool.isRequired,
  overrides: PropTypes.object.isRequired,
  resetOverrides: PropTypes.func.isRequired,
};

export default InventoryStep;
